import { ArrowLeft, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { spotById, spotImg } from "@/data/planner";

const SITE = "https://cphhighlights.com";

// A readable, crawlable day-by-day itinerary built straight from the planner's
// own spot data (real names, durations and curated notes), with a deep link
// that loads the exact days into the interactive planner. Shared by the themed
// landing pages (returning visitor, family, winter) so each stays accurate.
const plannerTrip = (dayGroups) =>
  `/planner?trip=any~${dayGroups.length}~${dayGroups.map((g) => g.join(",")).join(";")}`;

const StopRow = ({ id, index, note }) => {
  const spot = spotById(id);
  if (!spot) return null;
  const reverse = index % 2 === 1;
  const text = note || spot.note;
  return (
    <Reveal y={30}>
      <div
        data-testid={`themed-stop-${id}`}
        className="grid md:grid-cols-2 gap-5 md:gap-10 items-center py-6 md:py-8 border-b border-zinc-200/70 last:border-0"
      >
        <div className={`order-2 ${reverse ? "md:order-2" : "md:order-1"}`}>
          <h3 className="font-display font-900 tracking-tight leading-[1] text-2xl md:text-3xl">{spot.name}</h3>
          {text && (
            <p className="font-body text-base text-black/70 mt-3 leading-relaxed">{text}.</p>
          )}
        </div>
        <div className={`order-1 ${reverse ? "md:order-1" : "md:order-2"}`}>
          <div className="border border-zinc-200 shadow-brut overflow-hidden aspect-[4/3]">
            <img
              src={spotImg(id, 1000)}
              alt={spot.name + ", Copenhagen"}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
            />
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export function ThemedItinerary({ cfg }) {
  const heroImg = cfg.heroImg || spotImg(cfg.heroImgId || "nyhavn", 1600);
  const dayGroups = cfg.days.map((d) => d.ids);
  const tripUrl = plannerTrip(dayGroups);

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: cfg.schemaName, item: `${SITE}${cfg.seo.path}` },
    ],
  };
  const guide = {
    "@context": "https://schema.org",
    "@type": "TravelGuide",
    name: cfg.schemaName,
    url: `${SITE}${cfg.seo.path}`,
    description: cfg.seo.description,
    isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
    publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
  };
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: cfg.schemaName,
    itemListElement: cfg.days
      .flatMap((d) => d.ids)
      .map((id) => spotById(id))
      .filter(Boolean)
      .map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.name })),
  };

  return (
    <main data-testid={cfg.testid} className="pt-16 md:pt-20">
      <Seo title={cfg.seo.title} description={cfg.seo.description} path={cfg.seo.path} image={heroImg} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(guide)}</script>
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt={cfg.schemaName} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="themed-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton title={cfg.shareTitle} testid="themed-share" />
          </div>
          <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow mb-4 text-white">
            {cfg.eyebrow}
          </p>
          <h1 className="font-display font-900 tracking-tighter leading-[0.85] text-5xl sm:text-6xl lg:text-7xl">
            {cfg.h1}
            <br />
            <span className="font-editorial italic font-600">{cfg.h1Italic}</span>
          </h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6 leading-relaxed">{cfg.intro}</p>
        </div>
      </section>

      {/* optional note banner */}
      {cfg.topNote && (
        <section className="bg-[#FCEFC5] border-b border-black">
          <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-5 flex items-start gap-3">
            <MapPin size={20} className="shrink-0 mt-0.5 text-[#B8860B]" />
            <p className="font-body text-sm md:text-base text-black/80 leading-relaxed">
              <strong className="font-800 text-black">{cfg.topNote.title}</strong> {cfg.topNote.body}
            </p>
          </div>
        </section>
      )}

      {/* days */}
      {cfg.days.map((day, di) => (
        <section
          key={di}
          className={`${di % 2 === 0 ? "bg-white" : "bg-[#F9F8F6]"} border-t border-dotted border-zinc-300`}
        >
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-8 md:pb-10">
            <Reveal>
              <p className="inline-flex items-center bg-black text-white font-display font-900 text-sm tracking-[0.2em] px-4 py-1.5 rounded-full mb-4">
                {day.label}
              </p>
              <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
                {day.title}
                {day.titleAccent && (
                  <>
                    <br />
                    <span className="font-editorial italic font-600">{day.titleAccent}</span>
                  </>
                )}
              </h2>
              {(day.blurbNode || day.blurb) && (
                <p className="font-body text-base md:text-lg text-black/70 mt-4 max-w-2xl leading-relaxed">{day.blurbNode || day.blurb}</p>
              )}
            </Reveal>
            <div className="mt-6">
              {day.ids.map((id, i) => (
                <StopRow key={id} id={id} index={i} note={day.notes?.[id]} />
              ))}
            </div>
            {day.extras?.length > 0 && (
              <Reveal>
                <div data-testid={`themed-nearby-${di + 1}`} className="mt-6 bg-[#F4F2EE] rounded-xl p-5 md:p-6 border border-zinc-200">
                  <p className="font-body font-800 text-[11px] tracking-[0.25em] text-black/45 mb-3">ALSO NEARBY</p>
                  <ul className="space-y-2.5">
                    {day.extras.map((ex, xi) => {
                      if (ex && ex.node) {
                        return (
                          <li key={ex.key || `node-${xi}`} className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                            {ex.node}
                          </li>
                        );
                      }
                      const s = typeof ex === "string" ? spotById(ex) : ex;
                      if (!s || !s.name) return null;
                      const key = typeof ex === "string" ? ex : ex.name;
                      return (
                        <li key={key} className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                          <strong className="font-800 text-black">{s.name}</strong>
                          {s.note ? ` — ${s.note}.` : ""}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Reveal>
            )}
            <Reveal>
              <Link
                to={`/planner?trip=any~1~${day.ids.join(",")}`}
                data-testid={`themed-day-planner-${di + 1}`}
                className="inline-flex items-center gap-2 mt-7 font-display font-900 text-base bg-black text-white px-6 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-xl transition-all"
              >
                Build this day in the planner
              </Link>
            </Reveal>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="text-white border-t border-dotted border-zinc-300" style={{ backgroundColor: cfg.accent }}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            {cfg.cta.title}
            <br />
            <span className="font-editorial italic font-600">{cfg.cta.titleItalic}</span>
          </h2>
          <Link
            to={tripUrl}
            data-testid="themed-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-black text-white px-8 py-4 rounded-full border border-white/20 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            Open the whole plan in the planner
          </Link>
        </div>
      </section>

      <ExploreLinks testid={`explore-${cfg.testid}`} links={cfg.explore} />
      <ShareBar title={cfg.shareTitle} />
    </main>
  );
}
